import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { IntegratedOrder } from "@/lib/api";
import {
  formatIntegratedOrderDate,
  INTEGRATED_ORDER_SITUATION_LABELS,
  integratedOrderActionLabel,
  INTEGRATION_TYPE_LABELS,
  integratedOrderSituation,
  routedCategoryLabel,
} from "@/lib/integratedOrders";

interface IntegratedOrderDetailsDialogProps {
  order: IntegratedOrder | null;
  onClose: () => void;
}

// Corpo recebido do ERP, formatado quando for um JSON válido.
const formatPayload = (payload?: string | null) => {
  if (!payload) return null;
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

const Field = ({ label, value }: { label: string; value?: string | number | null }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="break-words text-sm font-medium">{value === null || value === undefined || value === "" ? "—" : value}</p>
  </div>
);

export function IntegratedOrderDetailsDialog({ order, onClose }: IntegratedOrderDetailsDialogProps) {
  const payload = formatPayload(order?.rawPayload);
  return (
    <Dialog open={order != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        {order && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Registro {order.id}
                <Badge variant={order.needsReview ? "destructive" : "default"}>
                  {INTEGRATED_ORDER_SITUATION_LABELS[integratedOrderSituation(order)]}
                </Badge>
              </DialogTitle>
              <DialogDescription>
                Pré-pedido {order.preOrderId ?? "—"} recebido em {formatIntegratedOrderDate(order.createdAt)}.
              </DialogDescription>
            </DialogHeader>

            {order.needsReview && order.reviewReason && (
              <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm">
                <p className="font-medium text-destructive">Motivo da revisão</p>
                <p className="mt-1">{order.reviewReason}</p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field label="Escola" value={order.clientName ? `${order.clientName} (${order.clientCode})` : order.clientCode} />
              <Field label="CNPJ/CPF recebido" value={order.cnpjCpf} />
              <Field label="Produto" value={order.productDescription ? `${order.productDescription} (${order.productCode})` : null} />
              <Field label="SKU recebido (ItemId)" value={order.itemId} />
              <Field label="Descrição recebida" value={order.description} />
              <Field label="Ciclo" value={order.cycleYear} />
              <Field label="Quantidade" value={order.quantity} />
              <Field
                label="Tipo de integração"
                value={order.integrationType != null ? `${order.integrationType} - ${INTEGRATION_TYPE_LABELS[order.integrationType] ?? ""}` : null}
              />
              <Field label="Pré-pedido" value={order.preOrderId} />
              <Field label="Referência do pré-pedido" value={order.preOrderReference} />
              <Field label="Ação da integração" value={integratedOrderActionLabel(order.integrationAction)} />
              <Field label="Roteado para" value={routedCategoryLabel(order.routedCategory)} />
              <Field label="Pedido programado" value={order.scheduledOrderNumber} />
              <Field label="Última revisão" value={order.reviewedAt ? formatIntegratedOrderDate(order.reviewedAt) : null} />
              <Field label="Revisado por" value={order.reviewedBy} />
            </div>

            {order.reviewComment && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm">
                <p className="text-xs text-muted-foreground">Comentário da revisão</p>
                <p className="mt-1 whitespace-pre-wrap">{order.reviewComment}</p>
              </div>
            )}

            {payload && (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Corpo recebido do ERP</p>
                <pre className="max-h-64 overflow-auto rounded-md border bg-muted/30 p-3 text-xs">{payload}</pre>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
